import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, ArrowUpRight, ArrowDownRight, Printer } from "lucide-react";
import { printConsignmentNote } from "@/lib/pdf";

export default function StoreDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/stores/${id}/summary`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="p-4 text-slate-500">Memuat...</div>;

  return (
    <>
      <Button variant="ghost" size="sm" onClick={()=>nav(-1)} className="mb-4" data-testid="btn-back"><ArrowLeft className="w-4 h-4 mr-1"/>Kembali</Button>

      <PageHeader
        title={data.store.name}
        subtitle={`${data.store.contact || "-"}  ·  ${data.store.address || "Tidak ada alamat"}`}
        testId="page-store-detail"
        actions={<Button onClick={()=>nav("/store-sales")} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Terima Laporan</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Total Disalurkan</div>
          <div className="font-mono-num text-xl font-semibold">{formatIDR(data.total_distributed)}</div>
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Total Diterima</div>
          <div className="font-mono-num text-xl font-semibold text-[var(--hotto-success)]">{formatIDR(data.total_paid)}</div>
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Nilai Stok Titipan</div>
          <div className="font-mono-num text-xl font-semibold text-[var(--hotto-primary)]">{formatIDR(data.stock_value)}</div>
        </Card>
        <Card className={`p-5 border-2 rounded-md stat-card ${data.outstanding_estimate>0?"border-[var(--hotto-alert)]":"border-[var(--hotto-border)]"}`}>
          <div className="overline mb-2">Selisih (Est. Piutang)</div>
          <div className={`font-mono-num text-xl font-semibold ${data.outstanding_estimate>0?"text-[var(--hotto-alert)]":"text-slate-700"}`}>
            {formatIDR(data.outstanding_estimate)}
          </div>
          <div className="text-xs text-slate-400 mt-1">Disalurkan - Diterima - Sisa Stok</div>
        </Card>
      </div>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Stok Titipan ({data.stock.length})</TabsTrigger>
          <TabsTrigger value="distributions" data-testid="tab-dist">Riwayat Penyaluran ({data.distributions.length})</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Riwayat Laporan ({data.sales.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="mt-4">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Produk</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga Toko</TableHead><TableHead className="text-right">Nilai</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.stock.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Tidak ada stok titipan.</TableCell></TableRow>}
                {data.stock.map((s) => (
                  <TableRow key={s.product_id} className="table-row-hover">
                    <TableCell className="font-medium flex items-center gap-2"><Package className="w-4 h-4 text-slate-400"/>{s.product?.name}</TableCell>
                    <TableCell className="text-slate-500 font-mono-num text-xs">{s.product?.sku || "-"}</TableCell>
                    <TableCell className="text-right font-mono-num">{s.qty} {s.product?.unit}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(s.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{formatIDR(Number(s.qty)*Number(s.unit_price))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="distributions" className="mt-4">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Item</TableHead><TableHead>Catatan</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
              <TableBody>
                {data.distributions.map((d) => (
                  <TableRow key={d.id} className="table-row-hover">
                    <TableCell className="font-mono-num text-sm">{formatDate(d.date)}</TableCell>
                    <TableCell className="text-slate-500">{d.items.length} item</TableCell>
                    <TableCell className="text-slate-500">{d.notes || "-"}</TableCell>
                    <TableCell className="text-right font-mono-num">
                      <Badge variant="outline" className="border-orange-300 text-orange-700"><ArrowUpRight className="w-3 h-3 mr-1"/>{formatIDR(d.total)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={()=>printConsignmentNote(d)} data-testid={`btn-print-dist-${d.id}`}>
                        <Printer className="w-4 h-4 text-[var(--hotto-primary)]"/>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Item</TableHead><TableHead>Metode</TableHead><TableHead className="text-right">Diterima</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.sales.map((s) => (
                  <TableRow key={s.id} className="table-row-hover">
                    <TableCell className="font-mono-num text-sm">{formatDate(s.date)}</TableCell>
                    <TableCell className="text-slate-500">{s.items.length} item</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-xs">{s.payment_method}</Badge></TableCell>
                    <TableCell className="text-right font-mono-num">
                      <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50"><ArrowDownRight className="w-3 h-3 mr-1"/>{formatIDR(s.total)}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
